import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Mail, MapPin, Info } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import emailjs from '@emailjs/browser';
import { supabase } from "@/integrations/supabase/client";
import { PackageSelection } from "./forms/PackageSelection";
import { ResolutionSelection } from "./forms/ResolutionSelection";
import { ContactInfo } from "./forms/ContactInfo";

const packages = [
  {
    id: "basic",
    name: "Basic",
    price: 150,
    photoLimit: 100,
    photoUpgrade: { cost: 30, maxPhotos: 175 },
    baseResolution: "1080p",
    resolutionUpgrades: [
      { resolution: "2.7k", price: 15 },
      { resolution: "4k", price: 35 },
      { resolution: "8k", price: 55 },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 175,
    photoLimit: 175,
    photoUpgrade: { cost: 45, maxPhotos: 225 },
    baseResolution: "2.7k",
    resolutionUpgrades: [
      { resolution: "4k", price: 30 },
      { resolution: "8k", price: 50 },
    ],
  },
  {
    id: "max",
    name: "Max",
    price: 220,
    photoLimit: 225,
    photoUpgrade: { cost: 30, maxPhotos: 265 },
    baseResolution: "2.7k",
    resolutionUpgrades: [
      { resolution: "4k", price: 20 },
      { resolution: "8k", price: 30 },
    ],
  },
  {
    id: "ultra",
    name: "Ultra",
    price: 250,
    photoLimit: null, // unlimited
    baseResolution: "4k",
    resolutionUpgrades: [
      { resolution: "8k", price: 15 },
    ],
  },
];

export const OrderForm = () => {
  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedResolution, setSelectedResolution] = useState("");
  const [photoUpgrade, setPhotoUpgrade] = useState(false);
  const [dateNeeded, setDateNeeded] = useState<Date | undefined>(undefined);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [referralSource, setReferralSource] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const ref = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    const initEmailJS = async () => {
      try {
        const { data: secrets, error } = await supabase.functions.invoke('get-emailjs-config', {
          body: {} // Add empty body to ensure proper request format
        });

        if (error) {
          console.error('Supabase function error:', error);
          throw error;
        }

        if (!secrets?.publicKey) {
          throw new Error('EmailJS configuration is incomplete');
        }

        emailjs.init(secrets.publicKey);
      } catch (error) {
        console.error('Error initializing EmailJS:', error);
        toast({
          title: "Error",
          description: "Failed to initialize email service. Please try again later.",
          variant: "destructive"
        });
      }
    };

    initEmailJS();
  }, [toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data: secrets, error: secretsError } = await supabase.functions.invoke('get-emailjs-config', {
        body: {} // Add empty body to ensure proper request format
      });
      
      if (secretsError) {
        console.error('Supabase function error:', secretsError);
        throw secretsError;
      }
      
      if (!secrets?.serviceId || !secrets?.templateId || !secrets?.publicKey) {
        throw new Error('EmailJS configuration is incomplete');
      }

      const orderDetails = `
Package: ${selectedPackage}
Date Needed By: ${dateNeeded ? dateNeeded.toLocaleDateString() : 'Not specified'}
Resolution: ${selectedResolution || 'Base Resolution'}
${photoUpgrade ? 'With Photo Upgrade' : 'Standard Photo Limit'}
Referral Source: ${referralSource || 'Not specified'}
Additional Details: ${message || 'None provided'}
Total Price: $${getPackagePrice()}`;

      // Send single email to admin with order summary
      await emailjs.send(secrets.serviceId, secrets.templateId, {
        to_email: "mymontage.video@gmail.com",
        from_name: name,
        from_email: email,
        from_phone: phone,
        subject: `New Order - ${selectedPackage} Package`,
        message: `New Order Received!\n\nCustomer Information:\nName: ${name}\nPhone: ${phone}\nEmail: ${email}\n\n${orderDetails}`
      }, secrets.publicKey);

      // Send auto-reply to customer
      await emailjs.send(secrets.serviceId, secrets.templateId, {
        to_email: email,
        to_name: name,
        from_name: "MyMontage",
        from_email: "mymontage.video@gmail.com",
        subject: "Your MyMontage Order Confirmation",
        message: `Dear ${name},\n\nThank you for choosing MyMontage! We have received your order and will review it shortly.\n\nOrder Details:\n${orderDetails}\n\nWe will contact you soon to discuss the next steps.\n\nBest regards,\nThe MyMontage Team`
      }, secrets.publicKey);

      toast({
        title: "Order Submitted Successfully!",
        description: "Check your email for order confirmation.",
        duration: 5000,
      });

      // Reset form
      setSelectedPackage("");
      setSelectedResolution("");
      setPhotoUpgrade(false);
      setDateNeeded(undefined);
      setName("");
      setPhone("");
      setEmail("");
      setReferralSource("");
      setMessage("");
    } catch (error) {
      console.error('Error processing order:', error);
      toast({
        title: "Error Submitting Order",
        description: "There was an error submitting your order. Please try again later.",
        duration: 5000,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPackagePrice = () => {
    const basePackage = packages.find(pkg => pkg.id === selectedPackage);
    if (!basePackage) return 0;
    
    let totalPrice = basePackage.price;

    if (selectedResolution && basePackage.resolutionUpgrades) {
      const upgrade = basePackage.resolutionUpgrades.find(
        u => u.resolution === selectedResolution
      );
      if (upgrade) totalPrice += upgrade.price;
    }

    if (photoUpgrade && basePackage.photoUpgrade) {
      totalPrice += basePackage.photoUpgrade.cost;
    }
    
    return totalPrice;
  };

  const getPhotoUpgradeCost = (packageId: string) => {
    const pkg = packages.find(p => p.id === packageId);
    return pkg?.photoUpgrade?.cost || 0;
  };

  const getPhotoUpgradeMax = (packageId: string) => {
    const pkg = packages.find(p => p.id === packageId);
    return pkg?.photoUpgrade?.maxPhotos || 0;
  };

  return (
    <section className="py-20 bg-gray-100" id="order">
      <div className="max-w-2xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl font-bold text-navy-800 mb-2">Order Your Montage</h2>
          <p className="text-gray-600">Create lasting memories with our professional montage service</p>
        </motion.div>

        <form className="space-y-6 bg-white p-8 rounded-lg shadow-lg" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <ContactInfo
              name={name}
              setName={setName}
              phone={phone}
              setPhone={setPhone}
              email={email}
              setEmail={setEmail}
            />

            <PackageSelection
              selectedPackage={selectedPackage}
              setSelectedPackage={setSelectedPackage}
              packages={packages}
              unlimitedPhotos={photoUpgrade}
              setUnlimitedPhotos={setPhotoUpgrade}
              getUnlimitedPhotoCost={getPhotoUpgradeCost}
            />

            <ResolutionSelection
              currentPackage={packages.find(pkg => pkg.id === selectedPackage)}
              selectedResolution={selectedResolution}
              setSelectedResolution={setSelectedResolution}
            />

            <motion.div className="transition-all duration-300" whileHover={{ scale: 1.02 }}>
              <Label className="flex items-center gap-2" htmlFor="dateNeeded">
                Date Montage Needed By
              </Label>
              <Input
                id="dateNeeded"
                type="date"
                value={dateNeeded ? dateNeeded.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateNeeded(e.target.value ? new Date(e.target.value) : undefined)}
                className="transition-all duration-300 hover:border-navy-600"
                required
              />
            </motion.div>
            
            <motion.div className="transition-all duration-300" whileHover={{ scale: 1.02 }}>
              <Label className="flex items-center gap-2" htmlFor="referralSource">
                <Info className="w-4 h-4" />
                How did you hear about us?
              </Label>
              <Input
                id="referralSource"
                value={referralSource}
                onChange={(e) => setReferralSource(e.target.value)}
                placeholder="Friend, Google, Social Media, etc."
                className="transition-all duration-300 hover:border-navy-600"
              />
            </motion.div>
            
            <div>
              <Label htmlFor="message">Additional Details</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Any specific requirements or details about your montage"
                className="min-h-[100px]"
              />
            </div>

            {selectedPackage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-navy-50 p-4 rounded-lg"
              >
                <p className="text-navy-800 font-semibold">
                  Total Price: ${getPackagePrice()}
                </p>
                <p className="text-sm text-navy-600 mt-2">
                  Estimated delivery: 1-2 weeks after order confirmation
                </p>
              </motion.div>
            )}
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-navy-600 text-white py-3 rounded-lg hover:bg-navy-700 transition-colors flex items-center justify-center gap-2"
          >
            <Mail className="w-5 h-5" />
            {isSubmitting ? "Submitting..." : "Submit Order"}
          </motion.button>
        </form>
      </div>
    </section>
  );
};
